import { type ReactNode } from 'react';

/**
 * StoreCardVisual — bloque visual superior de las cards de Tienda, SIN fotos.
 *
 * Reemplaza la imagen fotográfica por un bloque premium: gradiente por
 * categoría + glow + textura de grid + un icono semántico central (SVG inline,
 * estilo Lucide — el proyecto no usa librería de iconos, hace SVG a mano).
 * Mantiene la MISMA proporción (`aspect-[4/3]`) y presencia visual que la media
 * anterior, coherente con el fallback de marca de Home/Eventos.
 *
 * `resolveVisual(category, name)` mapea categoría + palabras clave del nombre a
 * un grupo (bebidas / gastronomía / fiesta / viajes / encuentro / retail) con
 * su icono y gradiente; cualquier caso no reconocido cae a un grupo genérico
 * coherente. `children` se renderiza como overlay (el badge de categoría).
 */

type IconProps = { className?: string };

function GlassIcon({ className }: IconProps): JSX.Element {
  // Copa de vino/cóctel — bebidas, licor, coctelería, vinos.
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="46"
      height="46"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M8 22h8" />
      <path d="M12 15v7" />
      <path d="M7 10h10" />
      <path d="M17 4H7l1 6a4 4 0 0 0 8 0z" />
    </svg>
  );
}

function ChefHatIcon({ className }: IconProps): JSX.Element {
  // Gorro de chef — gastronomía, comida, cenas, tablas, postres.
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="46"
      height="46"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6z" />
      <path d="M6 17h12" />
    </svg>
  );
}

function MusicIcon({ className }: IconProps): JSX.Element {
  // Música — fiesta, rumba, conciertos, entretenimiento.
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="46"
      height="46"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );
}

function PlaneIcon({ className }: IconProps): JSX.Element {
  // Avión — viajes, escapadas, hotelería.
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="46"
      height="46"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
    </svg>
  );
}

function UsersIcon({ className }: IconProps): JSX.Element {
  // Personas — encuentros, catas, clases, networking.
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="46"
      height="46"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function BagIcon({ className }: IconProps): JSX.Element {
  // Bolsa — merchandising, retail, lifestyle.
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="46"
      height="46"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

function SparklesIcon({ className }: IconProps): JSX.Element {
  // Genérico premium — categorías no reconocidas.
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="46"
      height="46"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3z" />
    </svg>
  );
}

type VisualGroup = {
  Icon: (props: IconProps) => JSX.Element;
  /** Clases de gradiente Tailwind para el fondo del bloque. */
  gradient: string;
  keywords: string[];
};

/**
 * Mapeo categoría/keyword → grupo visual. El orden importa: se evalúa de
 * arriba hacia abajo sobre `"<categoría> <nombre>"` normalizado.
 */
const GROUPS: VisualGroup[] = [
  {
    Icon: GlassIcon,
    gradient: 'from-[#7c3aed] via-[#a21caf] to-[#4c1d95]',
    keywords: [
      'bebida',
      'licor',
      'vino',
      'copa',
      'coctel',
      'cocteleria',
      'mixolog',
      'trago',
      'brindis',
      'cerveza',
      'whisky',
      'whiskey',
      'ron',
      'gin',
      'champ',
      'botella',
      'mojito',
      'aperitivo',
      'bar',
      'gastrobar',
    ],
  },
  {
    Icon: ChefHatIcon,
    gradient: 'from-[#f59e0b] via-[#d97706] to-[#7c2d12]',
    keywords: [
      'gastro',
      'comida',
      'cena',
      'plato',
      'tabla',
      'picada',
      'postre',
      'chef',
      'restaurant',
      'asado',
      'carne',
      'parrilla',
      'brunch',
      'desayuno',
      'almuerzo',
      'snack',
      'menu',
      'tapas',
      'maridaje',
      'cocina',
    ],
  },
  {
    Icon: MusicIcon,
    gradient: 'from-[#0076f4] via-[#7c3aed] to-[#00bED7]',
    keywords: [
      'fiesta',
      'rumba',
      'concierto',
      'evento',
      'show',
      'music',
      'dj',
      'baile',
      'celebrac',
      'entretenim',
      'discoteca',
      'party',
      'festival',
      'luces',
    ],
  },
  {
    Icon: PlaneIcon,
    gradient: 'from-[#0ea5e9] via-[#0891b2] to-[#155e75]',
    keywords: [
      'viaje',
      'escapada',
      'hotel',
      'tour',
      'vuelo',
      'avion',
      'destino',
      'playa',
      'cartagena',
      'cafetero',
      'hosped',
      'resort',
      'crucero',
    ],
  },
  {
    Icon: UsersIcon,
    gradient: 'from-[#10b981] via-[#059669] to-[#064e3b]',
    keywords: [
      'encuentro',
      'cata',
      'clase',
      'taller',
      'networking',
      'meet',
      'privad',
      'mixolo',
      'reserva',
    ],
  },
  {
    Icon: BagIcon,
    gradient: 'from-[#4f46e5] via-[#4338ca] to-[#312e81]',
    keywords: ['merch', 'retail', 'ropa', 'camiseta', 'souvenir', 'regalo', 'lifestyle'],
  },
];

const GENERIC: VisualGroup = {
  Icon: SparklesIcon,
  gradient: 'from-primary-500 via-primary-700 to-[#3b1d8a]',
  keywords: [],
};

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

function matchGroup(text: string): VisualGroup | null {
  const haystack = normalize(text);
  if (!haystack.trim()) return null;
  return GROUPS.find((g) => g.keywords.some((k) => haystack.includes(k))) ?? null;
}

/**
 * Resuelve el grupo visual (icono + gradiente) para una card de tienda.
 * El NOMBRE del producto manda sobre la categoría (más específico del ítem):
 * "Tabla de Picada" → gastronomía aunque la categoría sea "Coctelería". Si el
 * nombre no matchea, se usa la categoría; si tampoco, el grupo genérico.
 */
function resolveVisual(
  category: string | null | undefined,
  name: string | null | undefined,
): { Icon: (props: IconProps) => JSX.Element; gradient: string } {
  const group = matchGroup(name ?? '') ?? matchGroup(category ?? '') ?? GENERIC;
  return { Icon: group.Icon, gradient: group.gradient };
}

interface StoreCardVisualProps {
  category: string | null | undefined;
  name: string | null | undefined;
  /** Overlay (badge de categoría) posicionado en absoluto por el consumidor. */
  children?: ReactNode;
}

export function StoreCardVisual({ category, name, children }: StoreCardVisualProps): JSX.Element {
  const { Icon, gradient } = resolveVisual(category, name);

  return (
    <div
      className={`relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-gradient-to-br ${gradient} text-white/85`}
    >
      {/* Glow radial + acento cian para profundidad. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(70% 60% at 28% 18%, rgba(255,255,255,0.22) 0%, transparent 60%),' +
            'radial-gradient(60% 60% at 92% 100%, rgba(0,214,238,0.28) 0%, transparent 60%)',
        }}
      />
      {/* Textura de grid muy sutil. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      <Icon className="relative h-12 w-12 text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.4)] transition-transform duration-300 group-hover:scale-110" />
      {/* Velo inferior para legibilidad del badge superpuesto. */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent"
      />
      {children}
    </div>
  );
}
